/**
 * El motivo REAL de una Edge Function que falló.
 *
 * 🚨 DE DÓNDE SALE. El founder: «quise adjuntar el comprobante en Falabella y me sale que no se pudo
 *    adjuntar el comprobante, error en la función».
 *
 *    La función no había fallado por su cuenta: FALABELLA había rechazado la operación, y la función
 *    hizo lo correcto —devolver el mensaje del marketplace con un 502—. Lo que se perdía era el
 *    último tramo.
 *
 * 🪤 `supabase.functions.invoke` DESCARTA EL CUERPO CUANDO EL STATUS NO ES 2xx. En ese caso `data`
 *    viene en `null` y `error.message` es siempre el mismo texto genérico —«Edge Function returned a
 *    non-2xx status code»—, sin importar lo que la función haya contestado. El cuerpo sigue ahí, pero
 *    escondido en `error.context`, que es una `Response` sin leer.
 *
 *    O sea: cuanto MÁS se esfuerza una función en explicar el problema, MENOS se ve. Un 400 con
 *    «El comprobante todavía no fue aceptado por SUNAT» y un 500 por un bug se leen exactamente
 *    igual en la pantalla.
 *
 * ⭐ POR QUÉ ACÁ Y NO EN CADA LLAMADA. El patrón ya estaba copiado en cuatro pantallas —Ads,
 *    Productos, dos de WhatsApp— y faltaba en las 29 llamadas de Operaciones, que son justamente las
 *    que hablan con couriers y marketplaces: las que tienen algo que decir cuando fallan. Una regla
 *    que hay que acordarse de copiar es una regla que no se copia.
 */

/** La `Response` sin leer que supabase-js esconde en el error cuando el status no es 2xx. */
type ErrorConContexto = { context?: unknown; message?: string } | null | undefined;

/**
 * Lee el cuerpo del error y devuelve lo que la función quiso decir.
 *
 * 🪤 NUNCA LANZA. Si el cuerpo no se puede leer —ya consumido, no es JSON, la red se cortó— devuelve
 *    `null` y quien llama cae a su mensaje de siempre. Un helper de diagnóstico que rompe convierte
 *    un error explicable en uno peor.
 */
export async function motivoDeLaFuncion(fnErr: ErrorConContexto): Promise<string | null> {
  const ctx = fnErr?.context as { json?: () => Promise<unknown>; status?: number } | undefined;
  if (!ctx || typeof ctx.json !== 'function') return null;
  try {
    const cuerpo = (await ctx.json()) as { error?: unknown; message?: string } | null;
    const texto = String(cuerpo?.error ?? cuerpo?.message ?? '').trim();
    return texto || null;
  } catch {
    /*
     * Sin cuerpo legible, el status es lo único honesto que queda — y 502/503 tienen una lectura
     * distinta que un 4xx: no es que hiciste algo mal, es que el otro lado no contestó.
     */
    const status = ctx.status ?? 0;
    if (status >= 502) return `El servicio no respondió (${status}). Reintentá en un momento.`;
    return null;
  }
}

/**
 * El mensaje que va a la pantalla, en orden de qué tan específico es.
 *
 * `data.error` primero porque una función que contesta 200 con `{ error }` ya dijo lo suyo; después
 * el cuerpo escondido del no-2xx; y recién al final el genérico de supabase-js.
 */
export async function mensajeDeLaFuncion(
  fnErr: ErrorConContexto,
  data: { error?: string } | null | undefined,
  porDefecto: string,
): Promise<string> {
  return (data?.error ?? '').trim()
    || (await motivoDeLaFuncion(fnErr))
    || (fnErr?.message ?? '').trim()
    || porDefecto;
}
